import apiFetch from '../../../api/apiFetch';
import generateHeaders from '../../../api/myHeaders';
import { KPI_TYPE } from '../../HotAlerts/hotAlertsInterface';
import { SurveyDefaultDatePeriod, SurveyForConfigurationProps } from '../../Surveys/surveyInterface';
import {
    ConfiguratedHotAlerts,
    PersonalizedSurveyProps,
    SelectedQuestion,
    SelectedSurveyProps,
    SurveyQuestion,
} from './addSurveyInterface';

interface SurveyContentResponse {
    selectedQuestions: SelectedQuestion[];
    selectedFilters: SelectedQuestion[];
}

interface HotAlertConfigResponse {
    id: number;
    satisfaction?: Record<string, any> | null;
    openDataQuestionId?: number | null;
    firstnameQuestionId?: number | null;
    lastnameQuestionId?: number | null;
    phoneQuestionId?: number | null;
    emailQuestionId?: number | null;
    listingQuestionVariable?: number[] | null;
    detailQuestionVariableId?: number[] | null;
    status: { id: number; label: string; color: { color: string; fontColor: string }; isDefault: boolean; isFinal: boolean }[];
}

export interface SurveyEditionState {
    selectedSurvey: SelectedSurveyProps;
    personalizedSurvey: PersonalizedSurveyProps;
    selectedQuestions: SelectedQuestion[];
    selectedFilters: SelectedQuestion[];
    configuratedHotAlerts?: ConfiguratedHotAlerts;
    hotAlertConfigId?: number;
}

const toPersonalizedSurvey = (survey: SurveyForConfigurationProps): PersonalizedSurveyProps => ({
    id: survey.surveyid,
    shortName: survey.shortname,
    name: survey.name,
    display: survey.display,
    status: survey.status,
    updateDate: new Date(survey.lastModificationDate),
    schema: survey.schemaName,
    temporalFilterType: survey.temporalFilterType,
    defaultDatePeriod: survey.defaultDatePeriod ?? SurveyDefaultDatePeriod.CURRENT_MONTH,
    context: survey.context ?? '',
    hasDashboard: survey.canCreateDashboard,
    hasHotAlert: survey.canExportHotAlert,
    hasToAnonymateData: survey.hasToAnonymate,
    anonymateDays: survey.anonymizationLimitDays ?? undefined,
    hasAutomaticSynchronization: survey.hassynchactived,
});

// The stored configuration only keeps what was inserted in Korto tables: when Askia
// still knows the question, its full definition is restored (type, captions, responses).
const withAskiaDefinition = (items: SelectedQuestion[], askiaQuestions: SurveyQuestion[]): SelectedQuestion[] => {
    const askiaById = new Map(askiaQuestions.map((q) => [q.id, q]));

    return items.map((item) => ({ ...item, question: askiaById.get(item.question.id) ?? item.question }));
};

const toConfiguratedHotAlerts = (config: HotAlertConfigResponse): ConfiguratedHotAlerts => {
    // satisfaction is stored as a raw JSON object with snake_case keys
    const satisfaction = config.satisfaction && Object.keys(config.satisfaction).length > 0 ? config.satisfaction : undefined;

    return {
        openedQuestion: config.openDataQuestionId ?? undefined,
        firstName: config.firstnameQuestionId ?? undefined,
        lastName: config.lastnameQuestionId ?? undefined,
        phone: config.phoneQuestionId ?? undefined,
        mail: config.emailQuestionId ?? undefined,
        satisfaction: satisfaction ? {
            questionId: satisfaction.question_id ?? satisfaction.questionId,
            type: (satisfaction.type ?? KPI_TYPE.SATISFACTION) as KPI_TYPE,
            positiveModalityIds: satisfaction.positive_modality_ids ?? satisfaction.positiveModalityIds ?? [],
            negativeModalityIds: satisfaction.negative_modality_ids ?? satisfaction.negativeModalityIds ?? [],
        } : undefined,
        additionalQuestion: config.listingQuestionVariable ?? [],
        detailQuestions: config.detailQuestionVariableId ?? [],
        statuses: config.status.map((s) => ({
            id: s.id,
            tempId: `status-${s.id}`,
            label: s.label,
            isDefault: s.isDefault,
            isFinal: s.isFinal,
            colors: s.color,
        })),
    };
};

const loadSurveyForEdition = async (survey: SurveyForConfigurationProps, selectedSchema: string): Promise<SurveyEditionState> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const get = (url: string) => apiFetch(url, { method: 'GET', headers: generateHeaders(selectedSchema) });

    const [content, askiaQuestions, hotAlertConfig] = await Promise.all([
        get(`${apiUrl}/configuration/surveys/${survey.surveyid}/content`) as Promise<SurveyContentResponse>,
        // Askia may be unreachable: the stored configuration is then used as is
        (get(`${apiUrl}/askia/surveys/${survey.surveyid}/questions`) as Promise<SurveyQuestion[]>).catch(() => []),
        // 404 when the survey has no hot alert configuration yet
        (get(`${apiUrl}/survey/${survey.surveyid}/hot_alert_config/`) as Promise<HotAlertConfigResponse>).catch(() => undefined),
    ]);

    return {
        selectedSurvey: { id: survey.surveyid, name: survey.shortname, description: survey.name },
        personalizedSurvey: toPersonalizedSurvey(survey),
        selectedQuestions: withAskiaDefinition(content.selectedQuestions, askiaQuestions),
        selectedFilters: withAskiaDefinition(content.selectedFilters, askiaQuestions),
        configuratedHotAlerts: hotAlertConfig ? toConfiguratedHotAlerts(hotAlertConfig) : undefined,
        hotAlertConfigId: hotAlertConfig?.id,
    };
};

export default loadSurveyForEdition;
